/**
 * Convert a declarative shape description into an Ammo collision shape.
 *
 * Supported:
 *   { type: 'box', size: [x, y, z] }           // or halfExtents
 *   { type: 'sphere', radius }
 *   { type: 'capsule', radius, height }        // height = cylindrical section
 *   { type: 'cylinder', radius, height }
 *   { type: 'plane', normal, constant }        // infinite static plane
 *   { type: 'compound', parts: [...] }         // children accept position/quaternion
 *   { type: 'trimesh', geometry }              // static level geometry only
 *
 * Returns the native shape plus any native resources that must live as long as
 * the shape (compound children, trimesh backing, ...).
 */
export function createShape(Ammo, description) {
  if (!description?.type) {
    throw new Error('Physics: a collision shape description is required.');
  }

  switch (description.type) {
    case 'box': {
      const size = description.halfExtents ?? description.size?.map((value) => value / 2);
      if (!size || size.length !== 3) {
        throw new Error('Physics: box shapes need size:[x,y,z] or halfExtents:[x,y,z].');
      }
      const halfExtents = new Ammo.btVector3(size[0], size[1], size[2]);
      const shape = new Ammo.btBoxShape(halfExtents);
      Ammo.destroy(halfExtents);
      return { shape, auxiliaryShapes: [] };
    }

    case 'sphere': {
      const radius = description.radius ?? 0.5;
      return { shape: new Ammo.btSphereShape(radius), auxiliaryShapes: [] };
    }

    case 'capsule': {
      const radius = description.radius ?? 0.35;
      const height = description.height ?? 1;
      return { shape: new Ammo.btCapsuleShape(radius, height), auxiliaryShapes: [] };
    }

    case 'cylinder': {
      const radius = description.radius ?? 0.5;
      const height = description.height ?? 1;
      const halfExtents = new Ammo.btVector3(radius, height / 2, radius);
      const shape = new Ammo.btCylinderShape(halfExtents);
      Ammo.destroy(halfExtents);
      return { shape, auxiliaryShapes: [] };
    }

    case 'plane': {
      const normal = description.normal ?? [0, 1, 0];
      const nativeNormal = new Ammo.btVector3(normal[0], normal[1], normal[2]);
      const shape = new Ammo.btStaticPlaneShape(nativeNormal, description.constant ?? 0);
      Ammo.destroy(nativeNormal);
      return { shape, auxiliaryShapes: [] };
    }

    case 'compound': {
      const parts = description.parts ?? [];
      if (parts.length === 0) {
        throw new Error('Physics: compound shapes need at least one part.');
      }

      const shape = new Ammo.btCompoundShape();
      const auxiliaryShapes = [];

      for (const part of parts) {
        const built = createShape(Ammo, part);

        const transform = new Ammo.btTransform();
        transform.setIdentity();

        const position = part.position ?? [0, 0, 0];
        const origin = new Ammo.btVector3(position[0], position[1], position[2]);
        transform.setOrigin(origin);

        const rotation = part.quaternion ?? [0, 0, 0, 1];
        const orientation = new Ammo.btQuaternion(
          rotation[0],
          rotation[1],
          rotation[2],
          rotation[3],
        );
        transform.setRotation(orientation);

        shape.addChildShape(transform, built.shape);

        Ammo.destroy(origin);
        Ammo.destroy(orientation);
        Ammo.destroy(transform);

        // The compound does not own its children; Physics destroys everything
        // in `auxiliaryShapes` when the body is removed.
        auxiliaryShapes.push(built.shape, ...built.auxiliaryShapes);
      }

      return { shape, auxiliaryShapes };
    }

    case 'trimesh': {
      const geometry = description.geometry;
      if (!geometry?.isBufferGeometry) {
        throw new Error('Physics: trimesh shapes need a Three.js BufferGeometry.');
      }

      const triangleMesh = new Ammo.btTriangleMesh(true, false);
      const position = geometry.attributes.position;
      const index = geometry.index;
      const triangleCount = index ? index.count : position.count;
      const v0 = new Ammo.btVector3();
      const v1 = new Ammo.btVector3();
      const v2 = new Ammo.btVector3();

      const readVertex = (target, vertexIndex) => {
        target.setValue(position.getX(vertexIndex), position.getY(vertexIndex), position.getZ(vertexIndex));
      };

      for (let i = 0; i < triangleCount; i += 3) {
        const a = index ? index.getX(i) : i;
        const b = index ? index.getX(i + 1) : i + 1;
        const c = index ? index.getX(i + 2) : i + 2;
        readVertex(v0, a);
        readVertex(v1, b);
        readVertex(v2, c);
        triangleMesh.addTriangle(v0, v1, v2, false);
      }

      Ammo.destroy(v0);
      Ammo.destroy(v1);
      Ammo.destroy(v2);

      const shape = new Ammo.btBvhTriangleMeshShape(triangleMesh, true, true);
      return { shape, auxiliaryShapes: [triangleMesh] };
    }

    default:
      throw new Error(`Physics: unknown shape type "${description.type}".`);
  }
}
